import { RefreshCw } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { markRefreshed, startFreshness } from '@/lib/freshness'
import { useT } from '@/i18n'
import { ListStore, useStores } from '@/stores'
import type { LookupStore } from '@/stores/LookupStore'
import { clearQueryCache } from '@/stores/queryCache'
import { bumpRefresh } from '@/stores/refreshSignal'

/**
 * Fetches the data on screen again: the lists, the reference lookups and the
 * screens' own dropdowns and reports (`bumpRefresh`). `everything`
 * («Обновить») also drops the cache and refetches every lookup loaded so far;
 * the background refresh refetches only the lookups on screen. Typed form
 * values stay.
 */
export function refreshData(lookups: LookupStore, everything: boolean) {
  if (everything) clearQueryCache()
  ListStore.reloadLive()
  bumpRefresh()
  return everything ? lookups.refreshAll() : lookups.refreshInUse()
}

/** Keeps the signed-in shell's data fresh in the background (lib/freshness). */
export function useBackgroundRefresh() {
  const { lookups } = useStores()
  useEffect(() => startFreshness(() => void refreshData(lookups, false)), [lookups])
}

/**
 * «Обновить» in the top bar (client, ALFI TEST #2): everything on screen,
 * now. Data also refreshes by itself (useBackgroundRefresh).
 */
export const GlobalRefresh = observer(function GlobalRefresh() {
  const t = useT()
  const { lookups } = useStores()
  const [busy, setBusy] = useState(false)

  const refresh = () => {
    if (busy) return
    setBusy(true)
    markRefreshed()
    void refreshData(lookups, true).finally(() => setBusy(false))
  }

  return (
    <Button variant="ghost" size="icon" onClick={refresh} aria-label={t('layout.topbar.refresh')} title={t('layout.topbar.refresh')} aria-busy={busy}>
      <RefreshCw className={cn('size-4', busy && 'animate-spin')} />
    </Button>
  )
})
